/**
 * Sharing-scope control for an analytical variable artifact card.
 *
 * Surfaces a compact menu the epi can open from the artifact card to
 * promote / demote an analytical variable between session-only, user-wide, and org-wide
 * visibility. Promotion calls ``POST /knowledge/avs/{slug}/promote``;
 * the user / org the analytical variable moves into come from the verified Auth0 token
 * server-side, so this component only sends the slug + target scope.
 *
 * Three pieces of UX are intentional:
 *
 *  1. **"Still being worked on" pill** when the artifact has not yet been
 *     registered (``scope === undefined``). Promotion is hidden until the
 *     drafter has finalized the analytical variable and the KNOWLEDGE registration write
 *     has succeeded — otherwise the menu would offer to share something
 *     that doesn't exist yet.
 *  2. **Demotion guard surfacing.** The backend refuses demotion when
 *     other analytical variables anchor on this slug (HTTP 409). We render the directive
 *     error message verbatim — that text already names which consumers
 *     are blocking the move, which is exactly what the epi needs to see.
 *  3. **Auto-stale on success.** After a successful promotion we forward
 *     the new scope to the parent so the artifact list re-renders with
 *     the new chip without waiting for the next session push. The
 *     mutation's onSuccess also invalidates the knowledge query for the
 *     slug so any other surface picks up the change.
 */

import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useKnowledgeEntry, usePromoteAv } from '../api/knowledgeQueries';
import type { AvPromotionScope } from '../api/knowledgeTypes';
import type { EpiArtifactScope } from '../types/epi-chat-types';

export interface ArtifactSharingControlProps {
  slug: string | undefined;
  scope: EpiArtifactScope | undefined;
  /**
   * When non-null, the entry is a frozen ``av.<slug>.v<N>`` snapshot.
   * Frozen versions are immutable by design — consumers pin to them for a
   * stable handle while the live analytical variable keeps iterating — so the control
   * shows a read-only chip with the version label and disables the
   * Change menu. Promotion / demotion always operates on the live row;
   * the user can navigate to that row to change scope.
   */
  frozenVersion?: number | null;
  /** Called with the new scope after a successful promotion. */
  onScopeChange?: (scope: EpiArtifactScope) => void;
}

const SCOPE_LABEL: Record<EpiArtifactScope, string> = {
  session: 'Session-only',
  user: 'My sessions',
  org: 'Organization',
};

// Compact form for the chip ("visibility: org") — full ``SCOPE_LABEL`` is
// reserved for the menu where there's room for the readable phrasing.
const SCOPE_CHIP_VALUE: Record<EpiArtifactScope, string> = {
  session: 'session-only',
  user: 'my sessions',
  org: 'organization',
};

const SCOPE_HINT: Record<EpiArtifactScope, string> = {
  session: 'Only this session can see this analytical variable.',
  user: 'Visible to you across all your sessions.',
  org: 'Shared with your whole organization.',
};

const TARGET_SCOPES: AvPromotionScope[] = ['session', 'user', 'org'];

const SCOPE_CHIP_COLOR: Record<EpiArtifactScope, 'default' | 'info' | 'success'> = {
  session: 'default',
  user: 'info',
  org: 'success',
};

export const ArtifactSharingControl = ({
  slug,
  scope,
  frozenVersion,
  onScopeChange,
}: ArtifactSharingControlProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const promote = usePromoteAv();

  // When the caller has a slug but no local scope (legacy artifact card
  // events, sessions that opened before scope started flowing in artifact
  // payloads), fall back to KNOWLEDGE as the source of truth. The query
  // is only enabled in this fallback case so the typical "scope already
  // came in via the artifact event" path makes zero extra requests.
  const needsScopeLookup = Boolean(slug) && !scope && typeof frozenVersion !== 'number';
  const knowledgeEntry = useKnowledgeEntry(needsScopeLookup ? `av.${slug}` : null);
  const fetchedScope = knowledgeEntry.data?.scope;
  const resolvedScope: EpiArtifactScope | undefined =
    scope ??
    (fetchedScope === 'session' || fetchedScope === 'user' || fetchedScope === 'org'
      ? fetchedScope
      : undefined);

  if (!slug) {
    return (
      <Chip
        size="small"
        label="Still being worked on"
        variant="outlined"
        sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
        data-testid="artifact-sharing-pending"
      />
    );
  }
  // Lookup in flight — render the loading state explicitly so the user
  // doesn't briefly see "still being worked on" before the fetch resolves.
  if (!resolvedScope && knowledgeEntry.isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <CircularProgress size={10} />
        <Typography variant="caption" color="text.secondary">
          Resolving sharing…
        </Typography>
      </Stack>
    );
  }
  // Fetch resolved (or no fetch was needed) and there is genuinely no
  // registered scope yet — true pre-registration state.
  if (!resolvedScope) {
    return (
      <Chip
        size="small"
        label="Still being worked on"
        variant="outlined"
        sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
        data-testid="artifact-sharing-pending"
      />
    );
  }

  // Frozen snapshot view — chip is read-only and visually distinguished by
  // the lock glyph so the user immediately sees that this view isn't the
  // mutation surface. Demoting / promoting a frozen version would defeat
  // the point of pinning to it; consumers rely on av.<slug>.v<N> being a
  // stable handle.
  if (typeof frozenVersion === 'number') {
    return (
      <Chip
        size="small"
        icon={<LockIcon sx={{ fontSize: (theme) => theme.typography.caption.fontSize }} />}
        label={`visibility: ${SCOPE_CHIP_VALUE[resolvedScope]} · v${frozenVersion}`}
        color={SCOPE_CHIP_COLOR[resolvedScope]}
        variant="filled"
        sx={{
          height: 22,
          fontSize: (theme) => theme.typography.caption.fontSize,
          '& .MuiChip-icon': { marginLeft: 0.5 },
        }}
        data-testid={`artifact-sharing-${slug}-frozen`}
      />
    );
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePromote = (target: AvPromotionScope) => {
    setAnchorEl(null);
    if (target === resolvedScope) {
      return;
    }
    setErrorMessage(null);
    promote.mutate(
      { slug, targetScope: target },
      {
        onSuccess: (data) => {
          onScopeChange?.(data.newScope as EpiArtifactScope);
        },
        onError: (err) => {
          // The backend's directive error messages (consumer-blocked
          // demotion, auth refusal, unknown slug) are already user-meaningful;
          // surface them verbatim so the epi can act on them.
          const detail =
            err instanceof Error && 'body' in err
              ? safeExtractDetail((err as { body: unknown }).body) ?? err.message
              : err.message;
          setErrorMessage(detail);
        },
      }
    );
  };

  const open = Boolean(anchorEl);

  return (
    <Stack spacing={0.5} data-testid={`artifact-sharing-${slug}`}>
      {/* The chip is the click target — clicking it opens the visibility
          menu. A small chevron suffix communicates that the chip is
          interactive without adding a separate Change button next to it,
          which makes the card metadata strip read as a single line of
          status rather than a chip-plus-action pair. */}
      {/* The chip stays visually consistent with the static facts chips
          (same height / size / weight); the chevron icon + the cursor +
          the tooltip carry the affordance, so the visibility chip
          doesn't read as a different *kind* of element from the rest of
          the metadata strip. */}
      <Tooltip title="Click to change who can see this analytical variable" enterDelay={200}>
        <Chip
          size="small"
          label={`visibility: ${SCOPE_CHIP_VALUE[resolvedScope]}`}
          color={SCOPE_CHIP_COLOR[resolvedScope]}
          variant={resolvedScope === 'session' ? 'outlined' : 'filled'}
          clickable
          deleteIcon={
            promote.isPending ? (
              <CircularProgress size={10} sx={{ marginRight: 0.5 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 14 }} />
            )
          }
          // ``onDelete`` makes the deleteIcon render as part of the chip;
          // it's effectively the same click target as the chip itself.
          onDelete={promote.isPending ? undefined : handleOpen}
          onClick={promote.isPending ? undefined : handleOpen}
          disabled={promote.isPending}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? `artifact-sharing-menu-${slug}` : undefined}
          data-testid={`artifact-sharing-chip-${slug}`}
          sx={{
            height: 22,
            fontSize: (theme) => theme.typography.caption.fontSize,
            '& .MuiChip-deleteIcon': { marginRight: 0.25 },
          }}
        />
      </Tooltip>
      <Menu
        id={`artifact-sharing-menu-${slug}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ dense: true, sx: { paddingY: 0 } }}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        {/* Header explains exactly what this menu mutates. The slug + the
            current scope sit right at the top so the user knows what's
            about to change before they click an option. */}
        <Box sx={{ paddingX: 2, paddingY: 1.25 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.08em', display: 'block', lineHeight: 1.2 }}
          >
            Change visibility
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            fontFamily="monospace"
            noWrap
            sx={{ marginTop: 0.25 }}
          >
            {slug}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Currently: {SCOPE_LABEL[resolvedScope]}
          </Typography>
        </Box>
        <Divider />
        {TARGET_SCOPES.map((target) => {
          const isCurrent = target === resolvedScope;
          return (
            <MenuItem
              key={target}
              selected={isCurrent}
              disabled={isCurrent}
              onClick={() => handlePromote(target)}
              data-testid={`artifact-sharing-option-${slug}-${target}`}
              sx={{ paddingY: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isCurrent ? <CheckIcon fontSize="small" color="primary" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="body2" fontWeight={isCurrent ? 600 : 500}>
                      {SCOPE_LABEL[target]}
                    </Typography>
                    {isCurrent && (
                      <Chip
                        size="small"
                        label="current"
                        sx={{
                          height: 18,
                          fontSize: (theme) => theme.typography.caption.fontSize,
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Stack>
                }
                secondary={SCOPE_HINT[target]}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          );
        })}
      </Menu>
      {errorMessage && (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setErrorMessage(null)}
          data-testid={`artifact-sharing-error-${slug}`}
          sx={{ paddingY: 0.25, fontSize: (theme) => theme.typography.caption.fontSize }}
        >
          {errorMessage}
        </Alert>
      )}
    </Stack>
  );
};

/** Pull a human-readable message out of FastAPI's ``{"detail": "..."}`` body. */
const safeExtractDetail = (body: unknown): string | null => {
  if (typeof body !== 'string' || body.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    if (typeof parsed.detail === 'string') {
      return parsed.detail;
    }
  } catch {
    // Body wasn't JSON — fall through to literal display.
    return body;
  }
  return null;
};
